<template>
  <div 
    :style="pageStyle"
    class="pc-carts-container">
    <div
      v-if="showCartGuide"
      class="guide-mask"
    />
    <div class="breadcrumb-wrapper">
      <BreadCrumb class="breadcrumb">
        <BreadCrumbItem to="/">
          Home
        </BreadCrumbItem>
        <BreadCrumbItem>Cart</BreadCrumbItem>
      </BreadCrumb>
    </div>
    <div class="carts-content">
      <CartGuide
        v-if="showCartGuide"
        @close="closeCartGuide"
      />
      <MainInfo
        @tab-change="handleTabChange"
        @batch-select="handleBatchSelect"
        @fetch-list="handleUpdateList"
        :list="cartsList"
        :is-empty="isEmpty"
        :is-loading="loading"
      >
        <div
          v-if="loading"
          class="loading-wrapper"
        >
          <img
            :src="gifSrc"
            width="240px"
            alt=""
          >
        </div>
        <template v-else>
          <CartItem
            v-for="item in cartsList"
            @show-exceed-quantity-dialog="handleShowExceedQuantityDialog"
            @show-process-dialog="handleShowProcessDialog"
            @fetch-list="handleUpdateList"
            :key="item.cartId"
            :data="item"
            :list="cartsList"
            :currency-data="currencyData"
          />
          <div class="bottom-line">
            <div class="text">
              I'm a bottom line.
            </div>
          </div>
        </template>
      </MainInfo>
      <FixedInfo
        @show-process-dialog="handleShowProcessDialog"
        :allow-order-switch="allowOrderSwitch"
        :checked-list="checkedList"
        :currency-data="currencyData"
        :is-empty="isEmpty"
        :is-loading="loading"
      />
    </div>

    <!-- processing 提示弹窗 -->
    <ProcessingDialog
      :visible="processDialogVisible"
      @close="processDialogVisible = false"
    />

    <ExceedQuantityDialog 
      v-if="showExceedQuantityDialog"
      @close="handleCloseExceedQuantityDialog"
      :quantity="minOrderQuantity"
    />

    <AddressInfoDialog
      v-if="showAddressInfoDialog"
      :address-type="2"
      :current-country="currentCountry"
      :handle-close="handleCloseAddressInfoDialog"
      @click="stopClick"
      @save-address="saveAddress"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { usePlugin } from '@/hooks/usePlugin'
import { fetchData } from '@/utils/fetch'
import { bankersRound } from '@/utils/utils'
import { buriedPointUtil, gaAnalytics } from '../../../../utils/utils'
import BreadCrumb from '@/components/BreadCrumb/BreadCrumb.vue'
import BreadCrumbItem from '@/components/BreadCrumb/BreadCrumbItem.vue'
import MainInfo from './components/MainInfo.vue'
import FixedInfo from './components/FixedInfo.vue'
import CartItem from './components/CartItem.vue'
import CartGuide from './components/CartGuide.vue'
import gifUrl from '@/assets/H5/empty.gif'
import ProcessingDialog from './components/ProcessingDialog.vue'
import ExceedQuantityDialog from '@/components/ExceedQuantityDialog/index.vue'
import AddressInfoDialog from '@/components/Offer/Sourcing/components/AddressInfoDialog/index.vue'
import storage from '@/utils/storage'

const { $toast } = usePlugin()

const store = useStore()

const isEmpty = ref(false)

const cartsList = ref([])

const currencyData = ref(null)

const showCartGuide = ref(false)

const loading = ref(false)

const showExceedQuantityDialog = ref(false)

const minOrderQuantity = ref(0)

const checkedList = computed(() => cartsList.value.filter(item => item.checked))

const gifSrc = computed(() => (gifUrl + '?' + new Date().getTime()))

const getCurrencyData = computed(() => store.state.currencyData)

const pageStyle = computed(() => {
  return loading.value ? {
    height: 'calc(100vh - 220.5px - 64px)'
  }: {
    height: '100%'
  }
})

watch(getCurrencyData, value => {
  currencyData.value = value
})

const updateCountCount = () => {
  fetchData('/appb/b2b/cart/cartCount').then((res) => {
    if (res.code === 200) {
      store.commit('cart/SET_CART_COUNT', res.data)
    }
  })
}

const handleTabChange = (status) => {
  getCartList(status)
}

const getCartList = async (status = null) => {
  loading.value = true

  isEmpty.value = false
  const url = status ? `/appb/b2b/cart/cartList?page=1&pageSize=200&frontCartStats=${status}` : '/appb/b2b/cart/cartList?page=1&pageSize=200'

  const { code, data } = await fetchData(url)
  if (code === 200) {
    const list = data.rows || []

    let checkNum = 0
    cartsList.value = list.map((item, index) => {
      if ([0, 10].includes(item.cartStatus) && checkNum < 50) {
        item.checked = true
        checkNum++
      } else {
        item.checked = false
      }
      return item
    })
    cartsList.value = list.map(item => {
      updateProductSkuPrice(item)
      item.skuSubtotal = (bankersRound(currencyData?.value.rate * item.skuPriceRmb, 2) * item.skuQuantity).toFixed(2)
      return item
    })

    isEmpty.value = cartsList.value.length === 0

    setTimeout(() => {
      loading.value = false
      needCartGuide()
    }, 1000)
  }
}

const getCurrency = async () => {
  const { code, data } = await fetchData(
    '/dsp-app/alibaba-common/currencyList'
  )
  if (code === 200) {
    const _cache = storage.get('currencyData')
    let target
    if (_cache) {
      target = data.find(item => item.targetCurrency === _cache.targetCurrency)
    } else {
      target = data[0]
    }
    store.commit('setCurrencyData', target)
    storage.set('currencyData', target)
  }
}

const handleBatchSelect = (value) => {
  cartsList.value.forEach((item, index) => {
    if (value) {
      if (index < 50 && [0, 10].includes(item.cartStatus)) item.checked = true
    } else {
      item.checked = false
    }
  })
}

const handleUpdateList = async () => {
  await getCartList()
  updateCountCount()
}

const needCartGuide = () => {
  const pcCartsGuide = JSON.parse(localStorage.getItem('pcCartsGuide'))
  if (pcCartsGuide === 1) return

  showCartGuide.value = true
}

const closeCartGuide = () => {
  showCartGuide.value = false
  localStorage.setItem('pcCartsGuide', 1)
}

// 获取起批量对应价格
const getPriceByQuantity = (quantity, priceRange, obj) => {
  let validPrices = priceRange.filter(item => item.startQuantity <= quantity)
  if (validPrices.length === 0) {
    validPrices = [priceRange[0]]
  }

  const bestMatch = validPrices.reduce((prev, curr) =>
    curr.startQuantity > prev.startQuantity ? curr : prev
  )

  return Number(bestMatch.price)
}

// 更新同级商品下sku价格
const updateProductSkuPrice = (obj) => {
  // 先更新自身
  obj.skuPriceRmb = getPriceByQuantity(obj.skuQuantity, obj.skuPriceCnyRanges, obj)

  // 单个区间 return
  const skuPriceCnyRanges = obj.skuPriceCnyRanges || []
  if (skuPriceCnyRanges.length < 2) return

  // 获取已选中的同个商品级别下的sku加购总数
  let count = 0
  const checkedList = cartsList.value.filter(v => v.checked)
  const sameProductLevelSku = checkedList.filter(v => v.spuId === obj.spuId)
  sameProductLevelSku.forEach(item => {
    count += item.skuQuantity
  })

  if (count) {
    const skuPriceRmb = getPriceByQuantity(count, obj.skuPriceCnyRanges)
    // 同级sku同步更新金额
    sameProductLevelSku.forEach(item => item.skuPriceRmb = skuPriceRmb)
  }
}

const allowOrderSwitch = ref(0)
const getUserInfo = async () => {
  const { code, data } = await fetchData('/dsp-app/alibaba-inter/queryMyInfo', {
    method: 'post'
  })
  if (code === 200) {
    allowOrderSwitch.value = data?.allowOrderSwitch
  }
}

const processDialogVisible = ref(false)
const handleShowProcessDialog = () => {
  processDialogVisible.value = true
}

// 地址弹窗
const currentCountry = ref({})
const countryList = computed(() => store.state.countryInfo)
const countryObj = computed(() => store.state.currentCountry)

watch(
  [countryList, countryObj],
  (newData) => {
    if (newData) {
      const country = localStorage.getItem('setCurrentCountry')
        ? JSON.parse(localStorage.getItem('setCurrentCountry'))
        : ''
      if (country) {
        currentCountry.value = country
        return
      }
      if (newData[1]) {
        currentCountry.value = newData[1]
        return
      }
      currentCountry.value = newData[0][0]
    }
  },
  { immediate: true }
)

const showAddressInfoDialog = ref(false)
const handleCloseAddressInfoDialog = () => {
  showAddressInfoDialog.value = false
}

const stopClick = (event) => {
  event.stopPropagation()
}

const saveAddress = () => {
  $toast('Address saved successfully', { type: 'success' })
  showAddressInfoDialog.value = false
}

const handleShowExceedQuantityDialog = (val) => {
  minOrderQuantity.value = val
  showExceedQuantityDialog.value = true
}

const handleCloseExceedQuantityDialog = async () => {
  showExceedQuantityDialog.value = false
  const { code, data } = await fetchData('/appb/b2b/cart/cartList?page=1&pageSize=200')
  if (code === 200) {
    const list = data.rows || []

    let checkNum = 0
    cartsList.value = list.map((item, index) => {
      if ([0, 10].includes(item.cartStatus) && checkNum < 50) {
        item.checked = true
        checkNum++
      } else {
        item.checked = false
      }
      return item
    })
    cartsList.value = list.map(item => {
      updateProductSkuPrice(item)
      item.skuSubtotal = (bankersRound(currencyData?.value.rate * item.skuPriceRmb, 2) * item.skuQuantity).toFixed(2)
      return item
    })
  }
}

onMounted(async () => {
  buriedPointUtil({ eventId: 'PC501' })
  gaAnalytics('p_cart_page_view', 'p_cart_page_view')

  loading.value = true
  getUserInfo()
  await getCurrency()
  getCartList()
})
</script>

<style lang="less" scoped>
.pc-carts-container {
  --cart-width: 850px;

  .loading-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .guide-mask {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, .8);
    z-index: 999;
  }

  .breadcrumb-wrapper {
    display: flex;
    align-items: center;
    height: 48px;
    border-bottom: 1px solid var(--main-border-color);

    .breadcrumb {
      width: 1200px;
      margin: 0 auto;
    }
  }
  .carts-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    position: relative;
    width: 1200px;
    height: calc(100% - 48px - 24px);
    margin: 0 auto;
    margin-top: 24px;
    overflow: auto;
  }

  .bottom-line {
    display: flex;
    justify-content: flex-end;
    padding-right: 152px;
    margin-top: 64px;
    .text {
      position: relative;
      height: 16px;
      width: 195px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #b2b7c2;
      font-size: 12px;
      font-family: mon-500;
      &::after, &::before {
        content: '';
        position: absolute;
        width: 32px;
        height: 1px;
        background-color: #ebebeb;
      }
      &::after {
        right: 0;
      }
      &::before {
        left: 0;
      }
    }
  }
}
</style>
